import { useId } from 'react';
import { EMPTY_FILTERS, type ResourceFilters as Filters } from '@/lib/resource-profiles';
import '@/components/reading-experience.css';

interface Props {
  value: Filters;
  onChange: (value: Filters) => void;
  facets: Record<'component' | 'task' | 'type', string[]>;
  count: number;
  total: number;
  noun?: string;
}

export default function ResourceFilters({ value, onChange, facets, count, total, noun = 'resources' }: Props) {
  const id = useId();
  const active = Object.values(value).some(Boolean);
  return (
    <div className="ecm-library-controls" role="search" aria-label={`Find ${noun}`}>
      <div className="ecm-library-search">
        <div>
          <label htmlFor={`${id}-search`}>Find the detail you need</label>
          <p>Combine a search with the filters below.</p>
        </div>
        <div className="ecm-library-search-field">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="1.5" /><path d="m15.5 15.5 5 5" stroke="currentColor" strokeWidth="1.5" /></svg>
          <input id={`${id}-search`} type="search" value={value.query} onChange={(event) => onChange({ ...value, query: event.target.value })} placeholder="For example, validation or support" />
        </div>
      </div>
      <div className="ecm-library-facets">
        {(['component', 'task', 'type'] as const).filter((key) => facets[key].length > 1).map((key) => (
          <div key={key}>
            <label htmlFor={`${id}-${key}`}>{key === 'type' ? 'Content type' : key === 'task' ? 'Your task' : 'Component'}</label>
            <select id={`${id}-${key}`} value={value[key]} onChange={(event) => onChange({ ...value, [key]: event.target.value })}>
              <option value="">{key === 'type' ? 'All content types' : key === 'task' ? 'All tasks' : 'All components'}</option>
              {facets[key].map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </div>
        ))}
      </div>
      <div className="ecm-library-results">
        <p role="status" aria-atomic="true">Showing {count} of {total} {noun}</p>
        {active && <button type="button" onClick={() => onChange({ ...EMPTY_FILTERS })}>Clear all filters</button>}
      </div>
    </div>
  );
}
